import {
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import { IconButton, InputAdornment, TextFieldProps, Theme } from "@mui/material";
import { createTheme } from "@mui/material";
import { SelectValidator, TextValidator, ValidatorComponent, ValidatorComponentProps, ValidatorForm } from "react-material-ui-form-validator";
import { Ref, RefObject, useEffect, useId, useRef, useState } from "react";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { strongPasswordRegex } from "../utils/constants";
import { useSelector, useStore } from "react-redux";
import { RootState } from "../store";

export const inputTheme: Theme = createTheme({
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: "#F1F1F1",
          borderColor: "#F1F1F1",
          fontSize: "16px",
          overflow: "hidden",
          "&:hover": {
            backgroundColor: "#F1F1F1",
          },
          width: "100%",
        },
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: "#383D39",
          margin: "0 !important",
          borderRadius: 8,
          "&.Mui-error": {
            border: "2px solid #E19393 !important",
          },
        },

        input: {
          color: "rgba(72, 78, 74, 1)",
          padding: "26px 0 12px 12px !important",
          border: "2px solid transparent",
          borderRadius: "8px",
          "&::-webkit-input-placeholder": {
            opacity: "1 !important",
          },
          "&::placeholder": {
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: 16,
          },
          "&:focus": {
            color: "#383D39 !important",
            border: "2px solid #6A8487 !important",
          },
          "&:active": {
            border: "2px solid #6A8487 !important",
          },
          "&.Mui-focused": {
            border: "2px solid #6A8487 !important",
          },
        },
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '12px !important',
          fontWeight: "600 !important",
          color: "#484E4A !important",
          transform: "translate(16px, 10px) scale(0.85) !important",
        },
        asterisk: {
          display: "none",
          "&.Mui-error": {
            display: "none",
          },
        },
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: "#CB4848 !important",
          fontStyle: "normal",
          fontWeight: "bold",
          fontSize: 12,
          lineHeight: "140%",
          margin: "0 !important",
          backgroundColor: "white",
          paddingTop: "8px",
        },
      }
    },
  },
});

export type TextInputProps = TextFieldProps & ValidatorComponentProps;

const TextInput = ({ value, ref, ...props }: TextInputProps) => {
  const id = props.id || 'input-' + useId();
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={inputTheme}>
        <TextValidator
          {...props}
          inputProps={{
            ...props.inputProps,
            "aria-required": props["aria-required"],
          }}
          aria-required={undefined}
          id={id}
          InputProps={{
            className: 'text-field',
            disableUnderline: true,
            autoComplete: "off",
            ...props.InputProps,
          }}
          value={value || ""}
          InputLabelProps={{
            className: 'label',
            shrink: true,
            classes: {
              error: 'erroredLabel',
            },
          }}
          variant="filled"
          fullWidth={true}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

// MUI's TextInput has a Select variant, so this uses the same props as
// TextInput with the addition of "items", and uses the SelectValidator under the hood
export const SelectInput = ({ items, value, ...props }: any) => {
  //const inp = <input type="hidden"></input>

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={inputTheme}>
        <SelectValidator
          {...props}
          SelectProps={{
            className: 'text-field',
            disableUnderline: true,
          }}

          focused={false} // TODO: there's a bug where Select stays focused even when it shouldn't be...
          value={value || ""}
          InputLabelProps={{
            className: 'label',
            shrink: true,
            classes: {
              error: 'error-label',
            },
          }}
          variant="filled"
          fullWidth={true}
        >
          {items}
        </SelectValidator>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const MatcherMapping = new Map<string, any>();

export const PasswordInput = ({ value, ref, ...props }: TextInputProps) => {
  const validatorRef = useRef<ValidatorComponent & HTMLDivElement>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const passwordMatchRule = props.validators?.find(
    (rule: string | any) => (typeof rule == "string" || rule instanceof String)
      && rule.startsWith('passwordMatches'))?.split(':')[1];

  const newMatcherVal = useSelector((state: RootState) => {
    if (!passwordMatchRule) {
      return '';
    }

    return passwordMatchRule?.split('.').reduce((acc: any, key: string) => {
      return acc[key];
    }, state);
  });

  if (MatcherMapping.get(passwordMatchRule) !== newMatcherVal) {
    MatcherMapping.set(passwordMatchRule, newMatcherVal);
    validatorRef.current?.validate(value);
  }

  useEffect(() => {
    ValidatorForm.addValidationRule('passwordMatches', (value, selector) => {
      const otherValue = MatcherMapping.get(selector!);
      return value === otherValue || !value || !otherValue;
    });
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={inputTheme}>
        <TextValidator
          ref={validatorRef}
          InputProps={{
            className: 'text-field',
            inputProps: { pattern: strongPasswordRegex.source },
            disableUnderline: true,
            autoComplete: "off",
            type: showPassword ? 'text' : 'password',
            endAdornment:
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>,
          }}
          value={value || ""}
          {...props}
          InputLabelProps={{
            className: 'label',
            shrink: true,
            classes: {
              error: 'error-label',
            },
          }}
          variant="filled"
          fullWidth={true}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default TextInput;
